// frontend/src/components/Modals/UserApprovalModal.jsx

import React, { useState } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
} from 'reactstrap';
import { usersAPI } from '../../services/api';
import { PERMISSIONS, ROLE_NAMES } from '../../utils/permissions';

function UserApprovalModal({ user, onClose, onSuccess }) {
  const [role, setRole] = useState('sales');
  const [customPermissions, setCustomPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePermissionToggle = (permission) => {
    setCustomPermissions((prev) => {
      if (prev.includes(permission)) {
        return prev.filter((p) => p !== permission);
      } else {
        return [...prev, permission];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await usersAPI.approve(user.id, {
        role,
        custom_permissions: role === 'custom' ? customPermissions : [],
      });

      onSuccess();
    } catch (err) {
      console.error('Error aprobando usuario:', err);
      setError(err.response?.data?.error || 'Error al aprobar usuario');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal 
      isOpen={true} 
      toggle={onClose} 
      size="lg"
      className="modal-dialog-centered"
    >
      <ModalHeader toggle={onClose}>
        <i className="ni ni-check-bold me-2"></i>
        Aprobar Usuario
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Info del usuario */}
          <div className="card shadow border-0 mb-4">
            <div className="card-body p-3">
              <div className="d-flex align-items-center">
                {user.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt={user.full_name}
                    className="avatar avatar-lg rounded-circle me-3 shadow"
                  />
                ) : (
                  <div className="avatar avatar-lg bg-gradient-primary rounded-circle me-3 shadow">
                    <i className="ni ni-single-02 text-white" style={{ fontSize: '1.5rem', lineHeight: '48px' }}></i>
                  </div>
                )}
                <div>
                  <h5 className="mb-0">{user.full_name}</h5>
                  <p className="text-sm text-muted mb-0">{user.email}</p>
                  {user.phone && (
                    <p className="text-xs text-muted mb-0">
                      <i className="ni ni-mobile-button me-1"></i>
                      {user.phone}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {error && (
            <Alert color="danger" className="shadow-sm">
              <span className="alert-icon"><i className="ni ni-fat-remove"></i></span>
              <span className="alert-text">{error}</span>
            </Alert>
          )}

          {/* Selección de rol */}
          <FormGroup>
            <Label className="form-control-label text-uppercase text-xs font-weight-bold">
              <i className="ni ni-badge me-2"></i>
              Rol del Usuario <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              className="form-control-alternative"
              value={role}
              onChange={(e) => {
                setRole(e.target.value);
                if (e.target.value !== 'custom') {
                  setCustomPermissions([]);
                }
              }}
              required
            >
              <option value="facilitator">🎓 Facilitador</option>
              <option value="sales">💼 Ventas</option>
              <option value="admin">👔 Administrador</option>
              <option value="finance">💰 Finanzas</option>
              <option value="viewer">👁️ Visualizador</option>
              <option value="custom">⚙️ Personalizado</option>
            </Input>
            <small className="text-muted d-block mt-2">
              {role === 'facilitator' && '🎓 Solo acceso a módulo de Facilitación'}
              {role === 'sales' && '💼 Acceso completo excepto Administración'}
              {role === 'admin' && '👔 Acceso completo al sistema'}
              {role === 'finance' && '💰 Gestionar finanzas y facturas'}
              {role === 'viewer' && '👁️ Solo visualización, sin edición'}
              {role === 'custom' && '⚙️ Selecciona permisos específicos abajo'}
            </small>
          </FormGroup>

          {/* Permisos personalizados */}
          {role === 'custom' && (
            <div className="card shadow border-0 mt-3">
              <div className="card-header bg-gradient-primary">
                <h6 className="mb-0 text-white">
                  <i className="ni ni-settings me-2"></i>
                  Permisos Personalizados
                </h6>
              </div>
              <div className="card-body p-3">
                <div className="row">
                  {/* Dashboard */}
                  <div className="col-12 mb-3">
                    <h6 className="text-uppercase text-xs font-weight-bolder text-muted mb-2">
                      📊 Dashboard
                    </h6>
                    <div className="custom-control custom-checkbox">
                      <input
                        className="custom-control-input"
                        type="checkbox"
                        id="view_dashboard"
                        checked={customPermissions.includes('view_dashboard')}
                        onChange={() => handlePermissionToggle('view_dashboard')}
                      />
                      <label className="custom-control-label text-sm" htmlFor="view_dashboard">
                        Ver Dashboard
                      </label>
                    </div>
                  </div>

                  {/* Clientes */}
                  <div className="col-md-6 mb-3">
                    <h6 className="text-uppercase text-xs font-weight-bolder text-muted mb-2">
                      🏢 Clientes
                    </h6>
                    {['view_clients', 'create_clients', 'edit_clients', 'delete_clients'].map((perm) => (
                      <div className="custom-control custom-checkbox mb-2" key={perm}>
                        <input
                          className="custom-control-input"
                          type="checkbox"
                          id={perm}
                          checked={customPermissions.includes(perm)}
                          onChange={() => handlePermissionToggle(perm)}
                        />
                        <label className="custom-control-label text-sm" htmlFor={perm}>
                          {PERMISSIONS[perm]}
                        </label>
                      </div>
                    ))}
                  </div>

                  {/* Propuestas */}
                  <div className="col-md-6 mb-3">
                    <h6 className="text-uppercase text-xs font-weight-bolder text-muted mb-2">
                      📄 Propuestas
                    </h6>
                    {['view_quotes', 'create_quotes', 'edit_quotes', 'delete_quotes', 'upload_quotes'].map((perm) => (
                      <div className="custom-control custom-checkbox mb-2" key={perm}>
                        <input
                          className="custom-control-input"
                          type="checkbox"
                          id={perm}
                          checked={customPermissions.includes(perm)}
                          onChange={() => handlePermissionToggle(perm)}
                        />
                        <label className="custom-control-label text-sm" htmlFor={perm}>
                          {PERMISSIONS[perm]}
                        </label>
                      </div>
                    ))}
                  </div>

                  {/* Órdenes */}
                  <div className="col-md-6 mb-3">
                    <h6 className="text-uppercase text-xs font-weight-bolder text-muted mb-2">
                      📦 Órdenes
                    </h6>
                    {['view_orders', 'create_orders', 'edit_orders', 'delete_orders', 'upload_orders'].map((perm) => (
                      <div className="custom-control custom-checkbox mb-2" key={perm}>
                        <input
                          className="custom-control-input"
                          type="checkbox"
                          id={perm}
                          checked={customPermissions.includes(perm)}
                          onChange={() => handlePermissionToggle(perm)}
                        />
                        <label className="custom-control-label text-sm" htmlFor={perm}>
                          {PERMISSIONS[perm]}
                        </label>
                      </div>
                    ))}
                  </div>

                  {/* 🆕 FACILITACIÓN */}
                  <div className="col-md-6 mb-3">
                    <h6 className="text-uppercase text-xs font-weight-bolder text-muted mb-2">
                      🎓 Facilitación
                    </h6>
                    <div className="custom-control custom-checkbox mb-2">
                      <input
                        className="custom-control-input"
                        type="checkbox"
                        id="view_facilitation"
                        checked={customPermissions.includes('view_facilitation')}
                        onChange={() => handlePermissionToggle('view_facilitation')}
                      />
                      <label className="custom-control-label text-sm" htmlFor="view_facilitation">
                        {PERMISSIONS['view_facilitation']}
                      </label>
                    </div>
                  </div>

                  {/* Finanzas */}
                  <div className="col-md-6 mb-3">
                    <h6 className="text-uppercase text-xs font-weight-bolder text-muted mb-2">
                      💰 Finanzas
                    </h6>
                    {['view_finance', 'create_invoices', 'edit_invoices', 'delete_invoices', 'manage_finance'].map((perm) => (
                      <div className="custom-control custom-checkbox mb-2" key={perm}>
                        <input
                          className="custom-control-input"
                          type="checkbox"
                          id={perm}
                          checked={customPermissions.includes(perm)}
                          onChange={() => handlePermissionToggle(perm)}
                        />
                        <label className="custom-control-label text-sm" htmlFor={perm}>
                          {PERMISSIONS[perm]}
                        </label>
                      </div>
                    ))}
                  </div>

                  {/* Administración */}
                  <div className="col-md-6 mb-3">
                    <h6 className="text-uppercase text-xs font-weight-bolder text-muted mb-2">
                      ⚙️ Administración
                    </h6>
                    {['manage_users', 'approve_users'].map((perm) => (
                      <div className="custom-control custom-checkbox mb-2" key={perm}>
                        <input
                          className="custom-control-input"
                          type="checkbox"
                          id={perm}
                          checked={customPermissions.includes(perm)}
                          onChange={() => handlePermissionToggle(perm)}
                        />
                        <label className="custom-control-label text-sm" htmlFor={perm}>
                          {PERMISSIONS[perm]}
                        </label>
                      </div>
                    ))}
                  </div>

                  {/* Reportes */}
                  <div className="col-12">
                    <h6 className="text-uppercase text-xs font-weight-bolder text-muted mb-2">
                      📈 Reportes
                    </h6>
                    <div className="custom-control custom-checkbox">
                      <input
                        className="custom-control-input"
                        type="checkbox"
                        id="view_reports"
                        checked={customPermissions.includes('view_reports')}
                        onChange={() => handlePermissionToggle('view_reports')}
                      />
                      <label className="custom-control-label text-sm" htmlFor="view_reports">
                        {PERMISSIONS['view_reports']}
                      </label>
                    </div>
                  </div>
                </div>

                {customPermissions.length === 0 && (
                  <Alert color="warning" className="mt-3 mb-0">
                    <span className="alert-icon"><i className="ni ni-bell-55"></i></span>
                    <span className="alert-text">
                      Debes seleccionar al menos un permiso
                    </span>
                  </Alert>
                )}
              </div>
            </div>
          )}

          {/* Resumen de permisos */}
          {role !== 'custom' && (
            <Alert color="info" className="shadow-sm mt-3">
              <h6 className="alert-heading mb-2">
                <i className="ni ni-bullet-list-67 me-2"></i>
                Permisos del rol {ROLE_NAMES[role]}:
              </h6>
              <ul className="mb-0 ps-3">
                {role === 'facilitator' && (
                  <>
                    <li>✅ Ver Dashboard</li>
                    <li>✅ Ver Cotizaciones (solo lectura)</li>
                    <li>✅ Ver módulo de Facilitación</li>
                  </>
                )}
                {role === 'sales' && (
                  <>
                    <li>✅ Ver Dashboard</li>
                    <li>✅ Ver clientes</li>
                    <li>✅ Ver, crear y subir propuestas</li>
                    <li>✅ Ver y subir órdenes</li>
                    <li>✅ Ver Facilitación</li>
                    <li>✅ Ver análisis de ventas</li>
                  </>
                )}
                {role === 'admin' && (
                  <>
                    <li>✅ Ver, crear y editar clientes</li>
                    <li>✅ Ver, crear y editar propuestas</li>
                    <li>✅ Ver, crear y editar órdenes</li>
                    <li>✅ Ver Facilitación</li>
                    <li>✅ Ver y gestionar finanzas</li>
                    <li>✅ Ver reportes</li>
                  </>
                )}
                {role === 'finance' && (
                  <>
                    <li>✅ Ver Dashboard</li>
                    <li>✅ Ver clientes</li>
                    <li>✅ Ver propuestas</li>
                    <li>✅ Ver órdenes</li>
                    <li>✅ Ver Facilitación</li>
                    <li>✅ Gestionar finanzas y facturas</li>
                    <li>✅ Ver reportes</li>
                  </>
                )}
                {role === 'viewer' && (
                  <>
                    <li>👁️ Ver Dashboard</li>
                    <li>👁️ Ver clientes (solo lectura)</li>
                    <li>👁️ Ver propuestas (solo lectura)</li>
                    <li>👁️ Ver órdenes (solo lectura)</li>
                    <li>👁️ Ver Facilitación (solo lectura)</li>
                    <li>👁️ Ver cashflow</li>
                  </>
                )}
              </ul>
            </Alert>
          )}
        </ModalBody>

        <ModalFooter className="border-0">
          <Button color="secondary" onClick={onClose} disabled={loading}>
            <i className="ni ni-fat-remove me-2"></i>
            Cancelar
          </Button>
          <Button
            color="success"
            type="submit"
            disabled={loading || (role === 'custom' && customPermissions.length === 0)}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Aprobando...
              </>
            ) : (
              <>
                <i className="ni ni-check-bold me-2"></i>
                Aprobar Usuario
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default UserApprovalModal;
